import { useState } from 'react'
import { useForm } from 'react-hook-form'
import emailjs from '@emailjs/browser'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowUpRight, Check, Copy, Loader2, Send } from 'lucide-react'
import { profile, socials } from '@/data/site'
import { Button } from '@/components/ui/button'
import { SpotlightCard } from '@/components/ui/spotlight-card'
import { Reveal } from '@/components/ui/reveal'
import { socialIcon } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

interface FormData {
  name: string
  email: string
  subject: string
  message: string
}

// EmailJS credentials (public by design — they identify the template, not a secret)
const EMAILJS = {
  serviceId: 'service_w8uo3d9',
  templateId: 'template_mzwik7a',
  publicKey: 'jTrSpKHUKZ9oLj6Vs',
}

export function Contact() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [copied, setCopied] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    try {
      await emailjs.send(
        EMAILJS.serviceId,
        EMAILJS.templateId,
        { from_name: data.name, from_email: data.email, subject: data.subject, message: data.message, to_name: 'Keerthika' },
        EMAILJS.publicKey,
      )
      setStatus('success')
      reset()
    } catch (err) {
      setStatus('error')
      console.error('Email error:', err)
    }
  }

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(profile.email)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      window.location.href = `mailto:${profile.email}`
    }
  }

  return (
    <section id="contact" aria-labelledby="contact-title" className="relative py-24 md:py-32">
      <div className="container">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
          <Reveal>
            <p className="eyebrow mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-gradient-to-r from-blush to-lilac" aria-hidden="true" />
              Contact
            </p>
            <h2 id="contact-title" className="text-balance text-4xl font-medium tracking-tight text-white md:text-6xl">
              Let&apos;s build something <span className="text-gradient font-serif font-normal italic">cool</span> together.
            </h2>
            <p className="mt-6 max-w-md text-pretty text-lg leading-relaxed text-zinc-400">
              Supervising PhD students, hiring research interns, or working on a problem that crosses fields? I&apos;d
              love to hear about it.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-2">
              <div className="inline-flex h-12 max-w-full items-center rounded-full border border-white/10 bg-white/[0.04] pl-5 pr-2 transition hover:border-white/20">
                <a href={`mailto:${profile.email}`} className="truncate text-[15px] text-zinc-100 hover:text-white">
                  {profile.email}
                </a>
                <button
                  type="button"
                  onClick={copyEmail}
                  aria-label={copied ? 'Email copied' : 'Copy email address'}
                  className="ml-3 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/[0.06] text-zinc-300 transition hover:bg-white/[0.12] hover:text-white"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>

            <ul className="mt-8 grid gap-2 sm:max-w-md">
              {socials.map((s) => {
                const Icon = socialIcon[s.name]
                return (
                  <li key={s.name}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="me noopener noreferrer"
                      className="group flex items-center gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 transition hover:border-white/[0.14] hover:bg-white/[0.04]"
                    >
                      <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/[0.05]">
                        <Icon className="h-4 w-4 text-zinc-200" />
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm text-zinc-100">{s.name}</span>
                        <span className="font-mono text-xs text-zinc-500">{s.handle}</span>
                      </span>
                      <ArrowUpRight className="h-4 w-4 text-zinc-500 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-zinc-200" />
                    </a>
                  </li>
                )
              })}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <SpotlightCard className="p-6 md:p-8">
              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Name" error={errors.name?.message}>
                    <input
                      id="name"
                      autoComplete="name"
                      placeholder="Ada Lovelace"
                      className={inputCls(!!errors.name)}
                      aria-invalid={!!errors.name}
                      {...register('name', { required: 'Please add your name' })}
                    />
                  </Field>
                  <Field label="Email" error={errors.email?.message}>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@university.edu"
                      className={inputCls(!!errors.email)}
                      aria-invalid={!!errors.email}
                      {...register('email', {
                        required: 'Please add your email',
                        pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'That email looks off' },
                      })}
                    />
                  </Field>
                </div>
                <Field label="Subject" error={errors.subject?.message}>
                  <input
                    id="subject"
                    placeholder="Research collaboration"
                    className={inputCls(!!errors.subject)}
                    aria-invalid={!!errors.subject}
                    {...register('subject', { required: 'What is this about?' })}
                  />
                </Field>
                <Field label="Message" error={errors.message?.message}>
                  <textarea
                    id="message"
                    rows={6}
                    placeholder="Tell me a little about what you have in mind…"
                    className={cn(inputCls(!!errors.message), 'h-auto min-h-[150px] resize-y py-3 leading-relaxed')}
                    aria-invalid={!!errors.message}
                    {...register('message', { required: 'Please write a short message' })}
                  />
                </Field>

                <Button type="submit" size="lg" disabled={isSubmitting} className="w-full">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send message <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </Button>

                <AnimatePresence>
                  {status !== 'idle' && (
                    <motion.p
                      role="status"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className={cn(
                        'rounded-xl border px-4 py-3 text-sm',
                        status === 'success'
                          ? 'border-emerald-400/20 bg-emerald-400/10 text-emerald-200'
                          : 'border-rose-400/20 bg-rose-400/10 text-rose-200',
                      )}
                    >
                      {status === 'success'
                        ? "Message sent, thank you! I'll get back to you soon."
                        : `Something went wrong. Please try again or email me at ${profile.email}.`}
                    </motion.p>
                  )}
                </AnimatePresence>
              </form>
            </SpotlightCard>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

const inputCls = (invalid: boolean) =>
  cn(
    'h-12 w-full rounded-xl border bg-white/[0.03] px-4 text-[15px] text-zinc-100 placeholder:text-zinc-600 transition focus:bg-white/[0.05] focus:outline-none focus:ring-4',
    invalid
      ? 'border-rose-400/40 focus:border-rose-400/60 focus:ring-rose-400/10'
      : 'border-white/10 focus:border-lilac/50 focus:ring-lilac/10',
  )

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  const id = label.toLowerCase()
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm text-zinc-300">
        {label}
      </label>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-rose-300" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
